import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import './LiveTranslate.css';
import {
  TRANSLATE_DOMAINS as DOMAINS,
  TRANSLATE_LANGUAGES as LANGUAGES,
  loadTranslateSettings as loadSettings,
} from '../lib/translateSettings';

// apiBase: '' for same-origin relative (typical web deployment), or an
// absolute "https://your-backend.example.com" for a native/cross-origin
// build. This is the ONLY thing this module needs to know about its host
// app's networking — no import of a host-specific config module.
function wsUrl(apiBase, path) {
  if (apiBase) {
    return apiBase.replace(/^http/, 'ws') + path;
  }
  // Dev server (e.g. CRA's npm start): the dev-proxy competes with the
  // dev server's own WebSocket (HMR) upgrade handling on the same
  // underlying HTTP server, which makes proxying a *second* WS path
  // unreliable (works sometimes, silently fails with code 1006 other
  // times). Bypass it and connect straight to the local backend instead.
  if (window.location.hostname === 'localhost' && window.location.port === '3000') {
    return `ws://127.0.0.1:5001${path}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${path}`;
}

// Downsample Float32 mic samples (native AudioContext rate) to 16-bit PCM
// at 16kHz mono — Gemini's native input rate, so the server does no
// resampling at all for this transport.
function floatTo16kPCM16(float32, inRate) {
  const outRate = 16000;
  const ratio = inRate / outRate;
  const outLength = Math.floor(float32.length / ratio);
  const pcm16 = new Int16Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const srcIndex = Math.floor(i * ratio);
    let s = float32[srcIndex];
    s = Math.max(-1, Math.min(1, s));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return pcm16.buffer;
}

/**
 * Standalone in-person conversation translator: mic in, translated audio
 * + live captions out. No call/phone concept at all — talks directly to
 * the translate_service backend's /api/translate/live WebSocket.
 *
 * Language / persona prefs live in the app Settings tab (web + iOS +
 * Android Capacitor). This panel only starts/stops the session.
 *
 * Props:
 *   authToken, apiBase, onClose, onActiveChange — as before
 *   onOpenSettings — host switches to Settings tab (single prefs home)
 *   initialAction — 'settings' | 'toggle' | undefined (mount one-shot)
 *
 * Ref: { openSettings(), toggle() }
 */
const LiveTranslate = forwardRef(function LiveTranslate(
  { authToken, apiBase = '', onClose, initialAction, onActiveChange, onOpenSettings, selectedNumber = '' },
  ref
) {
  const [active, setActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [status, setStatus] = useState('Idle');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(() => loadSettings(selectedNumber));
  const [captions, setCaptions] = useState([]);

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const micStreamRef = useRef(null);
  const processorRef = useRef(null);
  const playCursorRef = useRef(0);
  const pendingSourcesRef = useRef(0);
  const captionsEndRef = useRef(null);

  useEffect(() => {
    captionsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [captions]);

  // Refresh prefs whenever the selected line changes (the panel also
  // remounts on open) so Settings edits and line switches apply to
  // both the chip label and the WS auth payload.
  useEffect(() => {
    setSettings(loadSettings(selectedNumber));
  }, [selectedNumber]);

  const playAudioChunk = useCallback((base64Pcm24) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const binary = atob(base64Pcm24);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) float32[i] = pcm16[i] / 0x8000;

    const buffer = ctx.createBuffer(1, float32.length, 24000);
    buffer.copyToChannel(float32, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now, playCursorRef.current);
    pendingSourcesRef.current += 1;
    setSpeaking(true);
    source.onended = () => {
      pendingSourcesRef.current = Math.max(0, pendingSourcesRef.current - 1);
      if (pendingSourcesRef.current === 0) setSpeaking(false);
    };
    source.start(startAt);
    playCursorRef.current = startAt + buffer.duration;
  }, []);

  const stop = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    if (wsRef.current) {
      try { wsRef.current.send(JSON.stringify({ type: 'stop' })); } catch (e) {}
      wsRef.current.close();
      wsRef.current = null;
    }
    playCursorRef.current = 0;
    pendingSourcesRef.current = 0;
    setActive(false);
    setListening(false);
    setSpeaking(false);
    setStatus('Idle');
  }, []);

  const start = useCallback(async () => {
    setError('');
    setCaptions([]);
    setStatus('Requesting microphone…');

    // Create AudioContext synchronously inside the user gesture (before any
    // await) so iOS does not suspend it. An AudioContext created after an
    // await lands outside the synchronous gesture handler on iOS, leaving
    // ctx.state === 'suspended' and ScriptProcessorNode.onaudioprocess
    // silently never fires — no audio ever reaches the server.
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioContextCtor();
    audioCtxRef.current = ctx;

    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = micStream;

      // Resume defensively — some iOS versions still suspend even when
      // the context was created inside the gesture.
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch (e) {}
      }

      playCursorRef.current = ctx.currentTime;

      setStatus('Connecting…');
      try {
        const healthUrl = apiBase ? `${apiBase}/api/translate/health` : '/api/translate/health';
        const healthRes = await fetch(healthUrl);
        if (healthRes.ok) {
          const health = await healthRes.json();
          if (!health.gemini_api_key_set) {
            setError('Translation not configured on server (GEMINI_API_KEY missing in backend/.env)');
            // Mic + AudioContext were already acquired above — release
            // them on this early return so the mic indicator doesn't
            // stay lit and the context isn't left suspended.
            stop();
            return;
          }
        }
      } catch (_) {}

      const ws = new WebSocket(wsUrl(apiBase, '/api/translate/live'));
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({
          token: authToken,
          user_lang: settings.userLang,
          remote_lang: settings.remoteLang,
          domain: settings.domain,
          auto_detect: settings.autoDetect,
        }));
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'ready') {
            setStatus('Translating…');
            setActive(true);
            setListening(true);

            // Wire up mic capture only once the server confirms readiness,
            // so no audio is dropped before Gemini's session is live.
            const source = ctx.createMediaStreamSource(micStream);
            const processor = ctx.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;
            processor.onaudioprocess = (e) => {
              if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
              const input = e.inputBuffer.getChannelData(0);
              const pcm16kBuf = floatTo16kPCM16(input, ctx.sampleRate);
              wsRef.current.send(pcm16kBuf);
            };
            // ScriptProcessorNode only fires onaudioprocess while connected
            // to a destination in some browsers — route through a
            // zero-gain node so the mic isn't audibly looped back locally.
            const silentGain = ctx.createGain();
            silentGain.gain.value = 0;
            source.connect(processor);
            processor.connect(silentGain);
            silentGain.connect(ctx.destination);
          } else if (msg.type === 'audio') {
            playAudioChunk(msg.data);
          } else if (msg.type === 'text') {
            setCaptions(prev => [...prev.slice(-19), { text: msg.text, id: Date.now() + Math.random() }]);
          } else if (msg.type === 'error') {
            setError(msg.error || 'Unknown error');
          }
        } catch (e) {
          console.error('[LiveTranslate] bad message', e);
        }
      };

      ws.onerror = (evt) => {
        console.error('[LiveTranslate] WS error event', evt);
        setError('Connection error');
      };

      ws.onclose = (evt) => {
        console.log('[LiveTranslate] WS closed code=', evt.code, 'reason=', evt.reason, 'wasClean=', evt.wasClean);
        setStatus('Idle');
        setActive(false);
        setListening(false);
        setSpeaking(false);
      };
    } catch (e) {
      console.error('[LiveTranslate] start failed', e);
      // AudioContext was created before this try block — clean it up on failure.
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      setError(e.message || 'Could not start');
      setStatus('Idle');
    }
  }, [apiBase, authToken, settings, playAudioChunk, stop]);

  const handleClose = () => {
    stop();
    onClose();
  };

  useEffect(() => {
    if (onActiveChange) onActiveChange(active);
  }, [active, onActiveChange]);

  const goToSettings = useCallback(() => {
    if (typeof onOpenSettings === 'function') {
      onOpenSettings();
      return;
    }
    onClose?.();
  }, [onOpenSettings, onClose]);

  useEffect(() => {
    if (initialAction === 'settings') goToSettings();
    else if (initialAction === 'toggle') start();
    // Mount-only: initialAction is a one-shot instruction from the host app.
  }, []); // intentionally empty — one-shot mount behavior

  useImperativeHandle(ref, () => ({
    openSettings: goToSettings,
    toggle: () => { if (active) stop(); else start(); },
  }), [active, start, stop, goToSettings]);

  return (
    <div className="translate-modal-overlay" onClick={handleClose}>
      <div className="translate-modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="translate-controls translate-setup">
          <div className="translate-header">
            <span>🎙️ Live Translate</span>
            <button className="translate-close" onClick={handleClose}>✕</button>
          </div>

          <button
            type="button"
            className="translate-pair-chip"
            onClick={goToSettings}
            disabled={active}
            title={active ? 'Stop translating to change languages' : 'Change languages in Settings'}
          >
            {LANGUAGES[settings.userLang]} ↔ {LANGUAGES[settings.remoteLang]} · {DOMAINS[settings.domain]}
            {!active && <span className="translate-pair-edit-hint"> Settings</span>}
          </button>

          <div className="translate-status-row">
            <span className={`translate-activity-dot ${listening && !speaking ? 'is-listening' : ''} ${speaking ? 'is-speaking' : ''}`} />
            <span className="translate-status">
              {speaking ? 'Speaking translation…' : listening ? 'Listening…' : status}
            </span>
          </div>
          {error && <div className="translate-warning">{error}</div>}

          {captions.length > 0 && (
            <div className="translate-captions">
              {captions.map(c => (
                <div key={c.id} className="translate-caption-line">{c.text}</div>
              ))}
              <div ref={captionsEndRef} />
            </div>
          )}

          {!active ? (
            <button className="btn-primary translate-start" onClick={start}>
              Start
            </button>
          ) : (
            <button className="btn-secondary translate-stop" onClick={stop}>
              Stop
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default LiveTranslate;
